import { useState, type FC } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, Loader2 } from "lucide-react";

import { supabase } from "@/shared/lib/supabase";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { FieldError } from "@/shared/components/ui/FieldError";
import { GoogleOAuthSection } from "@/shared/components/ui/GoogleOAuthSection";
import { startGoogleOAuth } from "@/shared/utils/googleOAuth";
import {
  LAWYER_ROUTES,
  AUTH_ROUTES,
  AUTH_QUERY_PARAMS,
  AUTH_SOURCES,
  PUBLIC_ROUTES,
} from "@/shared/constants/paths";
import { isValidEmail } from "@/shared/utils/validators";
import { setCachedLawyerStatus } from "@/shared/hooks/useIsLawyer";
import { useQueryClient } from "@tanstack/react-query";
import { sentryUtils } from "@/shared/utils/sentry";
import { cn } from "@/shared/utils/core";

import { AttorneyAuthLayout } from "@/pages/lawyer/signup/components/AttorneyAuthLayout";

const LawyerLoginPage: FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleSignIn = async () => {
    if (!isValidEmail(email) || !password) {
      setError(!isValidEmail(email) ? "Enter a valid email address" : "Enter your password");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) {
        setError(authError.message);
        return;
      }

      if (data.user) {
        const { data: dbUser, error: dbError } = await supabase
          .from("users")
          .select("user_id")
          .eq("auth_id", data.user.id)
          .single();

        if (dbError || !dbUser) {
          setError("Account not found. Please sign up first.");
          await supabase.auth.signOut();
          return;
        }

        const { data: lawyer } = await supabase
          .from("lawyers")
          .select("id")
          .eq("user_id", dbUser.user_id)
          .maybeSingle();

        setCachedLawyerStatus(!!lawyer);
        queryClient.invalidateQueries({ queryKey: ["app-user-data"] });
        queryClient.invalidateQueries({ queryKey: ["is-lawyer"] });

        if (lawyer) {
          queryClient.invalidateQueries({ queryKey: ["lawyer-persons"] });
          navigate(LAWYER_ROUTES.DASHBOARD);
        } else {
          navigate(PUBLIC_ROUTES.HOME);
        }
      }
    } catch (err) {
      setError("An unexpected error occurred");
      sentryUtils.captureError(err, "Lawyer login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    const oauthError = await startGoogleOAuth({
      type: "lawyer-login",
      redirectPath: LAWYER_ROUTES.LOGIN,
    });
    if (oauthError) {
      setError(oauthError);
    }
  };

  return (
    <AttorneyAuthLayout showSettlementBar>
      <h2 className="mb-2 text-center">Welcome back</h2>

      <GoogleOAuthSection label="Sign in with Google" onGoogleClick={handleGoogleSignIn} />

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-primary">Professional email</label>
        <Input
          type="email"
          placeholder="example@email.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => e.key === "Enter" && handleSignIn()}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-primary">Password</label>
        <div className="relative">
          <Input
            type={showPassword ? "text" : "password"}
            placeholder="Enter your password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => e.key === "Enter" && handleSignIn()}
            className={cn("pr-10", error && "border-destructive")}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary hover:text-primary"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        <Link
          to={`${AUTH_ROUTES.FORGOT_PASSWORD}?${AUTH_QUERY_PARAMS.FROM}=${AUTH_SOURCES.LAWYER}`}
          className="text-sm text-primary underline"
        >
          Forgot password?
        </Link>
      </div>

      <FieldError message={error} />

      <Button
        variant="brand"
        rounded={true}
        onClick={handleSignIn}
        disabled={loading}
        className="w-full"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            Log in <ArrowRight className="h-4 w-4" />
          </>
        )}
      </Button>

      <p className="text-center text-sm text-secondary">
        New to Elayne?{" "}
        <Link to={LAWYER_ROUTES.SIGNUP} className="text-brand underline">
          Create account
        </Link>
      </p>
    </AttorneyAuthLayout>
  );
};

export default LawyerLoginPage;
